import Invader from "../models/Invader";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export interface LeaderboardEntry {
  odri: string;
  pseudo: string;
  score: number | null;
  rank: number | null;
  siFound: number | null;
  cityFound: number | null;
  lastSyncDate: string | null;
}

export interface MonthlyStatsEntry {
  odri: string;
  pseudo: string | null;
  flashCount: number;
  pointsGained: number;
  newCities: string[];
}

export type CaptureSource = 'FLASH_INVADERS' | 'LOCAL';

export interface City {
  id: number;
  name: string;
  shortName: string;
  siCount: number | null;
}

/** Invader du catalogue complet (une ligne du stream NDJSON /api/invaders/stream). */
export interface CatalogInvader {
  id: string;
  status: string;
  hint: string | null;
  value: number | null;
  captured: boolean;
  captureSource: CaptureSource | null;
  imageUrl: string | null;
  spaceId: number | null;
  cityId: number | null;
}

export interface InvaderDetails {
  id: string;
  status: string;
  hint: string | null;
  value: number | null;
  lat: number;
  lng: number;
  imageUrl: string | null;
  imageUrlCredit: string | null;
  bgImageUrl: string | null;
  bgImageCredit: string | null;
  additionalImageUrl: string | null;
  additionalImageCredit: string | null;
  videoLocationUrl: string | null;
  videoLocationCredit: string | null;
  instagramUrl: string | null;
  cityName: string | null;
  captured: boolean;
  dateFlash: string | null;
  source: CaptureSource | null;
}

export class InvaderService {
  static async fetchCities(
    getAccessTokenSilently: () => Promise<string>
  ): Promise<City[]> {
    const accessToken = await getAccessTokenSilently();

    const response = await fetch(`${BASE_URL}/api/invaders/cities`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Erreur HTTP : ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Stream progressif du catalogue complet (NDJSON). Le callback reçoit les
   * invaders par lots, au fil de l'arrivée des chunks réseau. Les lignes
   * coupées entre deux chunks sont bufferisées.
   */
  static async fetchInvadersCatalog(
    getAccessTokenSilently: () => Promise<string>,
    onBatch: (invaders: CatalogInvader[]) => void
  ): Promise<void> {
    const accessToken = await getAccessTokenSilently();

    const response = await fetch(`${BASE_URL}/api/invaders/stream`, {
      method: "GET",
      headers: {
        "Content-Type": "application/x-ndjson",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Erreur HTTP : ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) return;

    const decoder = new TextDecoder("utf-8");
    let buffer = "";

    const parseLines = (text: string, isLast: boolean): CatalogInvader[] => {
      buffer += text;
      const lines = buffer.split("\n");
      buffer = isLast ? "" : lines.pop() ?? "";
      const batch: CatalogInvader[] = [];
      for (const line of lines) {
        if (!line.trim()) continue;
        try {
          batch.push(JSON.parse(line) as CatalogInvader);
        } catch (error) {
          console.error("Erreur lors du parsing NDJSON :", error);
        }
      }
      return batch;
    };

    while (true) {
      const { value, done } = await reader.read();
      const batch = parseLines(done ? "" : decoder.decode(value, { stream: true }), done);
      if (batch.length > 0) {
        onBatch(batch);
      }
      if (done) break;
    }
  }

  static async fetchInvaderDetails(
    getAccessTokenSilently: () => Promise<string>,
    invaderId: string
  ): Promise<InvaderDetails> {
    const accessToken = await getAccessTokenSilently();

    const response = await fetch(`${BASE_URL}/api/invaders/${invaderId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(`L'invader ${invaderId} n'existe pas`);
      }
      throw new Error(`Erreur HTTP : ${response.status}`);
    }

    return await response.json();
  }

  static async markAsTook(
      getAccessTokenSilently: () => Promise<string>,
      invaderId: string
  ): Promise<void> {
    try {
      const accessToken = await getAccessTokenSilently();

      const response = await fetch(`${BASE_URL}/api/invaders/${invaderId}/mark-as-captured`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP : ${response.status}`);
      }

      console.log("Invader marked as took successfully");
    } catch (error) {
      console.error("Erreur lors de la requête :", error);
      throw error;
    }
  };

  static async updateValue(
      getAccessTokenSilently: () => Promise<string>,
      invaderId: string,
      value: number
  ): Promise<void> {
    try {
      const accessToken = await getAccessTokenSilently();

      const response = await fetch(`${BASE_URL}/api/invaders/${invaderId}/value`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ value }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP : ${response.status}`);
      }

      console.log("Invader value updated successfully");
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la valeur :", error);
      throw error;
    }
  };

  static async updateStatus(
      getAccessTokenSilently: () => Promise<string>,
      invaderId: string,
      status: string
  ): Promise<void> {
    try {
      const accessToken = await getAccessTokenSilently();

      const response = await fetch(`${BASE_URL}/api/invaders/${invaderId}/status`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP : ${response.status}`);
      }

      console.log("Invader status updated successfully");
    } catch (error) {
      console.error("Erreur lors de la mise à jour du statut :", error);
      throw error;
    }
  };

  static async fetchStream(
      getAccessTokenSilently: () => Promise<string>,
      onNewInvader: (invader: Invader) => void
  ): Promise<void> {
    try {
      const accessToken = await getAccessTokenSilently();

      const response = await fetch(`${BASE_URL}/api/invaders/stream`, {
        method: "GET",
        headers: {
          "Content-Type": "application/x-ndjson",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP : ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder("utf-8");

      if (reader) {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.trim().split("\n");

          for (const line of lines) {
            try {
              const item = JSON.parse(line);
              const newInvader = new Invader(
                  item.id,
                  [item.lat, item.lng],
                  item.status,
                  item.instagramUrl,
                  item.captured,
                  item.value,
                  item.captureSource,
                  item.imageUrl
              );
              // Appeler le callback pour chaque invader reçu
              onNewInvader(newInvader);
            } catch (error) {
              console.error("Erreur lors du parsing JSON :", error);
            }
          }
        }
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des données :", error);
      throw error;
    }
  };
  static async fetchLeaderboard(
    getAccessTokenSilently: () => Promise<string>
  ): Promise<LeaderboardEntry[]> {
    try {
      const accessToken = await getAccessTokenSilently();

      const response = await fetch(`${BASE_URL}/api/invaders/leaderboard`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Erreur lors de la récupération du leaderboard :", error);
      throw error;
    }
  }

  static async fetchMonthlyStats(
    getAccessTokenSilently: () => Promise<string>,
    year: number,
    month: number
  ): Promise<MonthlyStatsEntry[]> {
    try {
      const accessToken = await getAccessTokenSilently();

      const response = await fetch(`${BASE_URL}/api/stats/monthly?year=${year}&month=${month}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Erreur lors de la récupération des stats mensuelles :", error);
      throw error;
    }
  }

  static async addInvaderById(
    getAccessTokenSilently: () => Promise<string>,
    invaderId: string
  ): Promise<string> {
    try {
      const accessToken = await getAccessTokenSilently();

      const response = await fetch(`${BASE_URL}/api/invaders/${invaderId}/mark-as-captured`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Invader ${invaderId} n'existe pas`);
        }
        throw new Error(`Erreur HTTP : ${response.status}`);
      }

      return await response.text();
    } catch (error) {
      console.error("Erreur lors de l'ajout de l'invader:", error);
      throw error;
    }
  }

  static async removeInvader(
    getAccessTokenSilently: () => Promise<string>,
    invaderId: string
  ): Promise<string> {
    try {
      const accessToken = await getAccessTokenSilently();

      const response = await fetch(`${BASE_URL}/api/invaders/${invaderId}/remove-captured`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP : ${response.status}`);
      }

      return await response.text();
    } catch (error) {
      console.error("Erreur lors de la suppression de l'invader:", error);
      throw error;
    }
  }
}